import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import {
  Activity,
  BadgeDollarSign,
  FileText,
  LayoutDashboard,
  LifeBuoy,
  Mail,
  Search,
  SquareCheckBig,
  Target,
  UsersRound,
  type LucideIcon,
} from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { listSpaces, type SpaceListItem } from '../api/auth';
import { listTasks } from '../api/tasks';
import { helpdeskApi } from '../api/helpdesk';
import { useFeather } from './useFeather';
import { RONDO_MENU, type MenuItem, type SubItem } from './platform-menu';
import { NotificationBell } from './NotificationBell';
import { GlobalSearch } from './GlobalSearch';
import { MobileMenu } from './MobileMenu';
import { BugReportModal } from './BugReportModal';
import { spaceSwitchUrl } from './space-switch';
import '../components/arc/arc-refresh.css';

// Space switcher action: navigate to the target Space's SUBDOMAIN host (the
// active Space is host-derived, so switching Space = changing host). Backend
// ACL is the SoT - this only routes; the dropdown is already access-gated by
// listSpaces(). No-op for the current Space or a Space without a subdomain.
function navigateToSpace(targetSubdomain: string | null, activeSubdomain: string | null): void {
  const { protocol, hostname, port } = window.location;
  const url = spaceSwitchUrl(targetSubdomain, activeSubdomain, { protocol, hostname, port });
  if (url) window.location.assign(url);
}

interface Props {
  menu?: MenuItem[];
  currentSpaceSlug?: string;
  activeModuleId: string;
  activeChildId?: string;
  isTasksActive: boolean;
  isHelpdeskActive?: boolean;
  showTasks?: boolean;
  showHelpdesk?: boolean;
  onSelectModule: (item: MenuItem) => void;
  onSelectChild: (item: MenuItem, child: SubItem) => void;
  onOpenTasks: () => void;
  onOpenTask: (taskId: number) => void;
  onOpenHelpdesk?: () => void;
  onOpenSettings: () => void;
  onOpenCrony?: () => void;
  showCrony?: boolean;
  pageTitle?: string;
  pageActions?: ReactNode;
  arcRefresh?: boolean;
  children: ReactNode;
}

function SpaceSwitcherIcon({ space }: { space: SpaceListItem | null }) {
  const slug = space?.slug;
  const label = slug === 'affilmaster' ? 'AM' : slug === 'affilplatform' ? 'AP' : (space?.name ?? 'S').charAt(0).toUpperCase();
  return (
    <span className={`prototype-space-badge prototype-space-${slug ?? 'default'}`}>
      <span>{label}</span>
    </span>
  );
}

const SPACE_DESCRIPTION: Record<string, string> = {
  dmwk: 'Agency workspace',
  rondo: 'Content & rewards',
  affilmaster: 'Affiliate management',
  affilplatform: 'B2B affiliate platform',
};

const ARC_NAV_ICONS: Record<string, LucideIcon> = {
  grid: LayoutDashboard,
  activity: Activity,
  target: Target,
  users: UsersRound,
  'dollar-sign': BadgeDollarSign,
  mail: Mail,
  'file-text': FileText,
};

function toggleTheme() {
  const cur = document.documentElement.getAttribute('data-bs-theme') || 'light';
  const next = cur === 'light' ? 'dark' : 'light';
  document.documentElement.setAttribute('data-bs-theme', next);
  try {
    localStorage.setItem('theme', next);
  } catch {
    /* private mode */
  }
}

export function PlatformShell({
  menu = RONDO_MENU,
  currentSpaceSlug,
  activeModuleId,
  activeChildId,
  isTasksActive,
  isHelpdeskActive,
  showTasks = true,
  showHelpdesk = true,
  onSelectModule,
  onSelectChild,
  onOpenTasks,
  onOpenTask,
  onOpenHelpdesk,
  onOpenSettings,
  onOpenCrony,
  showCrony = false,
  pageTitle,
  pageActions,
  arcRefresh = false,
  children,
}: Props) {
  const { me, logout } = useAuth();
  const topnavRef = useRef<HTMLElement>(null);
  const [topnavCompact, setTopnavCompact] = useState(false);
  useLayoutEffect(() => {
    const el = topnavRef.current;
    if (!el) return;
    const measure = () => {
      const had = el.classList.contains('is-compact');
      el.classList.remove('is-compact');
      const rect = el.getBoundingClientRect();
      const next = el.nextElementSibling?.getBoundingClientRect();
      const overflow = el.scrollWidth > el.clientWidth + 1 || (!!next && next.width > 0 && rect.right > next.left + 1);
      if (had) el.classList.add('is-compact');
      setTopnavCompact(overflow);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [menu]);
  const [spaces, setSpaces] = useState<SpaceListItem[]>([]);
  const [openTaskCount, setOpenTaskCount] = useState(0);
  const [waitingHelpdeskCount, setWaitingHelpdeskCount] = useState(0);
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [bugOpen, setBugOpen] = useState(false);

  useEffect(() => {
    const cls = currentSpaceSlug && ['affilmaster', 'rondo', 'affilplatform', 'dmwk'].includes(currentSpaceSlug)
      ? `p1-${currentSpaceSlug}-redesign`
      : null;
    if (!cls) return;
    // Overlays (global search, task detail, modals) are siblings of the shell
    // wrapper. Keep the shared redesign scope on <body> as well so those
    // surfaces receive the same tokens and typography as the active Space.
    document.body.classList.add('p1-redesign', cls);
    return () => document.body.classList.remove('p1-redesign', cls);
  }, [currentSpaceSlug]);

  useEffect(() => {
    if (!arcRefresh) return;
    document.body.classList.add('p1-arc-refresh');
    return () => document.body.classList.remove('p1-arc-refresh');
  }, [arcRefresh]);

  useEffect(() => {
    listSpaces()
      .then(setSpaces)
      .catch(() => setSpaces([]));
    const myId = me?.userId;
    const activeSpaceId = me?.activeSpaceId ?? null;
    listTasks()
      .then((t) =>
        setOpenTaskCount(
          t.filter(
            (x) =>
              x.assigneeUserId === myId &&
              x.status !== 'done' &&
              x.status !== 'cancelled' &&
              (activeSpaceId == null || x.primarySpaceId === activeSpaceId),
          ).length,
        ),
      )
      .catch(() => setOpenTaskCount(0));
    if (showHelpdesk) {
      helpdeskApi
        .list({ status: 'ceka_na_uziv' })
        .then((r) =>
          setWaitingHelpdeskCount(
            r.tickets.filter((t) => activeSpaceId == null || String(t.primarySpaceId) === String(activeSpaceId)).length,
          ),
        )
        .catch(() => setWaitingHelpdeskCount(0));
    } else {
      setWaitingHelpdeskCount(0);
    }
  }, [me?.userId, me?.activeSpaceId, showHelpdesk]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useFeather([activeModuleId, activeChildId, spaces.length, pageTitle]);

  const activeModule = menu.find((m) => m.id === activeModuleId) ?? null;
  const homeModule = menu.find((m) => m.id === 'dashboard') ?? menu[0] ?? null;
  const subItems = activeModule?.children ?? [];

  const activeSpace =
    (currentSpaceSlug ? spaces.find((s) => s.slug === currentSpaceSlug) : undefined) ??
    spaces.find((s) => s.id === me?.activeSpaceId) ??
    spaces[0] ??
    null;
  const displayName = me?.displayName ?? (me ? `user #${me.userId}` : '');
  const initials = (() => {
    if (me?.displayName) {
      const w = me.displayName.split(/\s+/).filter(Boolean);
      return ((w[0]?.[0] ?? '') + (w[1]?.[0] ?? (w[0]?.[1] ?? ''))).toUpperCase() || '-';
    }
    if (me?.email) return me.email.slice(0, 2).toUpperCase();
    return me ? `U${me.userId}`.slice(0, 2).toUpperCase() : '-';
  })();

  return (
    <>
      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} spaceSlug={currentSpaceSlug} />
      <BugReportModal open={bugOpen} onClose={() => setBugOpen(false)} />
      <MobileMenu
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        spaces={spaces}
        activeSpace={activeSpace}
        onNavigateSpace={(subdomain) => navigateToSpace(subdomain, activeSpace?.subdomain ?? null)}
        menu={menu}
        activeModuleId={activeModuleId}
        activeChildId={activeChildId}
        onSelectModule={onSelectModule}
        onSelectChild={onSelectChild}
        onOpenTasks={onOpenTasks}
        onOpenHelpdesk={onOpenHelpdesk}
        onOpenSettings={onOpenSettings}
        onLogout={() => void logout()}
        displayName={displayName}
        email={me?.email ?? ''}
        showTasks={showTasks}
        showHelpdesk={showHelpdesk}
      />
      <div
      className={`hk-wrapper${currentSpaceSlug && ['affilmaster', 'rondo', 'affilplatform', 'dmwk'].includes(currentSpaceSlug) ? ` p1-redesign p1-${currentSpaceSlug}-redesign` : ''}${arcRefresh ? ' p1-arc-refresh' : ''}`}
      data-layout="horizontal"
      data-layout-style="default"
      data-menu="light"
      data-footer="simple"
    >
      <nav className="hk-navbar navbar navbar-expand-xl navbar-light fixed-top">
        <div className="container-fluid">
          <div className="nav-start-wrap">
            {isTasksActive && (
              <button
                type="button"
                className="btn btn-icon btn-rounded btn-flush-dark flush-soft-hover module-nav-toggle"
                aria-label="Menu modulu"
                onClick={() => window.dispatchEvent(new CustomEvent('p1:open-module-nav'))}
              >
                <span className="icon">
                  <span className="feather-icon">
                    <i data-feather="menu"></i>
                  </span>
                </span>
              </button>
            )}
            <a
              className="navbar-brand brand-text d-flex align-items-center"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                if (homeModule) onSelectModule(homeModule);
              }}
              aria-label="Platform 1 – domů"
            >
              {arcRefresh ? (
                <img className="brand-logo brand-logo--arc-p1" src="/assets/platform1-piktogram.png" alt="Platform 1" />
              ) : (
                <>
                  <img className="brand-logo brand-logo--full d-none d-xl-block" src="/assets/platform1-logo.png" alt="Platform 1" />
                  <img className="brand-logo brand-logo--pikto d-xl-none" src="/assets/platform1-piktogram.png" alt="Platform 1" />
                </>
              )}
            </a>

            <button
              type="button"
              className="p1-mobile-space-trigger"
              aria-label={`Aktivní space: ${activeSpace?.name ?? 'Platform 1'}. Otevřít menu.`}
              onClick={() => setMobileMenuOpen(true)}
            >
              <SpaceSwitcherIcon space={activeSpace} />
              <span className="feather-icon" aria-hidden="true"><i data-feather="chevron-down"></i></span>
            </button>

            <div className="dropdown project-switcher ms-3 d-xl-block d-none" id="project-switcher">
              <button
                className="btn btn-sm d-inline-flex align-items-center project-switcher-btn dropdown-toggle no-caret"
                type="button"
                data-bs-toggle="dropdown"
                data-bs-auto-close="true"
                aria-expanded="false"
              >
                <SpaceSwitcherIcon space={activeSpace} />
                <span className="feather-icon ms-1">
                  <i data-feather="chevron-down"></i>
                </span>
              </button>
              <ul className="dropdown-menu project-switcher-menu" style={{ minWidth: 245 }}>
                {spaces.length === 0 ? (
                  <li><span className="dropdown-item-text text-muted">žádné Spaces</span></li>
                ) : (
                  spaces.map((s) => (
                    <li key={s.id}>
                      <span
                        className={`dropdown-item project-item${s.id === activeSpace?.id ? ' active' : ''}`}
                        role="button"
                        onClick={() => navigateToSpace(s.subdomain, activeSpace?.subdomain ?? null)}
                      >
                        <SpaceSwitcherIcon space={s} />
                        <span className="prototype-space-copy"><strong>{s.name}</strong><small>{SPACE_DESCRIPTION[s.slug] ?? s.slug}</small></span>
                        {s.id === activeSpace?.id && (
                          <span className="feather-icon fs-7 text-primary ms-2">
                            <i data-feather="check"></i>
                          </span>
                        )}
                      </span>
                    </li>
                  ))
                )}
              </ul>
            </div>

            <button
              id="global-search-btn"
              type="button"
              className="btn btn-icon btn-rounded btn-flush-dark flush-soft-hover ms-2 d-none d-xl-inline-flex"
              title="Hledat (Cmd+K)"
              onClick={(e) => {
                e.preventDefault();
                setSearchOpen(true);
              }}
            >
              <span className="icon">
                {arcRefresh ? <Search aria-hidden="true" /> : (
                  <span className="feather-icon">
                    <i data-feather="search"></i>
                  </span>
                )}
              </span>
            </button>
          </div>

          <nav ref={topnavRef} className={`platform-topnav d-xl-flex d-none ready${topnavCompact ? ' is-compact' : ''}`} role="navigation" aria-label="Projektové menu">
            {menu.map((item) => {
              const isActive = item.id === activeModuleId;
              const ArcIcon = arcRefresh ? ARC_NAV_ICONS[item.icon] : undefined;
              return (
                <a
                  key={item.id}
                  href="#"
                  className={`pnav-item${isActive ? ' active' : ''}`}
                  title={topnavCompact || arcRefresh ? item.label : undefined}
                  onClick={(e) => {
                    e.preventDefault();
                    onSelectModule(item);
                  }}
                >
                  <span className="pnav-icon" aria-hidden="true">
                    {ArcIcon ? <ArcIcon /> : <i data-feather={item.icon}></i>}
                  </span>
                  <span className="pnav-label">{item.label}</span>
                </a>
              );
            })}
          </nav>

          <div className="nav-right-wrap">
            <div className="nav-standalone-group d-xl-flex d-none">
              {showTasks && (
                <a
                  href="#"
                  className="nav-standalone-btn"
                  title="Úkoly"
                  aria-label="Úkoly"
                  onClick={(e) => {
                    e.preventDefault();
                    onOpenTasks();
                  }}
                  style={isTasksActive ? { background: 'var(--bs-tertiary-bg)' } : undefined}
                >
                  <span className="feather-icon" aria-hidden="true">
                    {arcRefresh ? <SquareCheckBig /> : <i data-feather="check-square"></i>}
                  </span>
                  <span>Úkoly</span>
                  {openTaskCount > 0 && (
                    <span className="nav-badge bg-primary-subtle text-primary">{openTaskCount}</span>
                  )}
                </a>
              )}
              {showHelpdesk && (
                <a
                  href="#"
                  className="nav-standalone-btn"
                  title="Helpdesk"
                  aria-label="Helpdesk"
                  onClick={(e) => {
                    e.preventDefault();
                    onOpenHelpdesk?.();
                  }}
                  style={isHelpdeskActive ? { background: 'var(--bs-tertiary-bg)' } : undefined}
                >
                  <span className="feather-icon" aria-hidden="true">
                    {arcRefresh ? <LifeBuoy /> : <i data-feather="life-buoy"></i>}
                  </span>
                  <span>Helpdesk</span>
                  {waitingHelpdeskCount > 0 && (
                    <span className="nav-badge bg-primary-subtle text-primary">{waitingHelpdeskCount}</span>
                  )}
                </a>
              )}
            </div>

            <div className="nav-end-wrap">
              <ul className="navbar-nav flex-row">
                <li className="nav-item">
                  <NotificationBell onOpenTask={onOpenTask} onOpenTasks={onOpenTasks} />
                </li>

                <li className="nav-item">
                  <button
                    type="button"
                    className="btn btn-icon btn-rounded btn-flush-dark flush-soft-hover bug-report-trigger"
                    title="Nahlásit bug / námět na rozvoj"
                    aria-label="Nahlásit bug"
                    onClick={(e) => { e.preventDefault(); setBugOpen(true); }}
                  >
                    <span className="icon">
                      <span className="position-relative">
                        <span className="feather-icon">
                          <svg
                            viewBox="0 0 24 24"
                            width="18"
                            height="18"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                          >
                            <path d="M8 2l1.88 1.88" />
                            <path d="M14.12 3.88L16 2" />
                            <path d="M9 7.13v-1a3 3 0 1 1 6 0v1" />
                            <path d="M12 20c-3.3 0-6-2.7-6-6v-3a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v3c0 3.3-2.7 6-6 6z" />
                            <path d="M12 20v-9" />
                            <path d="M6.53 9C4.6 8.8 3 7.1 3 5" />
                            <path d="M6 13H2" />
                            <path d="M3 21c0-2.1 1.7-3.9 3.8-4" />
                            <path d="M20.97 5c0 2.1-1.6 3.8-3.5 4" />
                            <path d="M22 13h-4" />
                            <path d="M17.2 17c2.1.1 3.8 1.9 3.8 4" />
                          </svg>
                        </span>
                      </span>
                    </span>
                  </button>
                </li>

                <li className="nav-item">
                  <div className="dropdown ps-2">
                    <a
                      className="dropdown-toggle no-caret"
                      href="#"
                      role="button"
                      data-bs-display="static"
                      data-bs-toggle="dropdown"
                      data-bs-auto-close="outside"
                      aria-expanded="false"
                      onClick={(e) => e.preventDefault()}
                    >
                      <div className="prototype-nav-avatar">
                        {me?.avatarUrl ? (
                          <img src={me.avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                        ) : (
                          <span className="initial-wrap">{initials}</span>
                        )}
                      </div>
                    </a>
                    <div className="dropdown-menu dropdown-menu-end" style={{ minWidth: 240 }}>
                      <div className="px-3 py-2">
                        <div className="fw-semibold">{displayName}</div>
                        <div className="fs-8 text-muted">{me?.email ?? `tenant #${me?.tenantId}`}</div>
                      </div>
                      <div className="dropdown-divider"></div>
                      {showCrony && onOpenCrony && (
                        <a
                          className="dropdown-item"
                          href="/crony"
                          role="button"
                          onClick={(e) => {
                            e.preventDefault();
                            onOpenCrony();
                          }}
                        >
                          <span className="dropdown-icon feather-icon">
                            <i data-feather="activity"></i>
                          </span>
                          <span>Crony</span>
                        </a>
                      )}
                      <a
                        className="dropdown-item"
                        href="#"
                        role="button"
                        onClick={(e) => {
                          e.preventDefault();
                          onOpenSettings();
                        }}
                      >
                        <span className="dropdown-icon feather-icon">
                          <i data-feather="settings"></i>
                        </span>
                        <span>Nastavení</span>
                      </a>
                      <a
                        className="dropdown-item"
                        href="#"
                        role="button"
                        onClick={(e) => {
                          e.preventDefault();
                          toggleTheme();
                        }}
                      >
                        <span className="dropdown-icon feather-icon">
                          <i data-feather="moon"></i>
                        </span>
                        <span>Přepnout motiv</span>
                      </a>
                      <a
                        className="dropdown-item"
                        href="#"
                        role="button"
                        onClick={(e) => {
                          e.preventDefault();
                          void logout();
                        }}
                      >
                        <span className="dropdown-icon feather-icon">
                          <i data-feather="log-out"></i>
                        </span>
                        <span>Odhlásit se</span>
                      </a>
                    </div>
                  </div>
                </li>

                <li className="nav-item">
                  <button
                    type="button"
                    className="btn btn-icon btn-rounded btn-flush-dark flush-soft-hover navbar-toggle d-xl-none"
                    aria-label="Menu"
                    onClick={() => setMobileMenuOpen(true)}
                  >
                    <span className="icon">
                      <span className="feather-icon">
                        <i data-feather="align-left"></i>
                      </span>
                    </span>
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </nav>

      <div className="hk-pg-wrapper">
        {/* Hidden for the Úkoly module: prod renders /ukoly as a full-bleed
            standalone module with its own left sidebar, no module sub-nav. */}
        {subItems.length > 0 && activeModule && !isTasksActive && (
          <nav className={`platform-subnav ready${subItems.length === 0 ? ' empty' : ''}`} aria-label="Kategorie modulu">
            <div className="subnav-inner">
              {subItems.map((c) => (
                <a
                  key={c.id}
                  href="#"
                  className={`subnav-item${c.id === activeChildId ? ' active' : ''}`}
                  onClick={(e) => {
                    e.preventDefault();
                    onSelectChild(activeModule, c);
                  }}
                >
                  {c.label}
                </a>
              ))}
            </div>
          </nav>
        )}
        <div className="container-xxl">
          {pageActions && (
            <div className="hk-pg-header pg-header-wth-tab pt-5 d-flex align-items-center justify-content-between">
              {pageActions && <div className="d-flex align-items-center gap-2">{pageActions}</div>}
            </div>
          )}
          <div className="hk-pg-body">{children}</div>
        </div>
      </div>
      <nav className="p1-mobile-dock" aria-label="Rychlá navigace">
        {homeModule && (
          <button
            type="button"
            className={activeModuleId === homeModule.id && !isTasksActive && !isHelpdeskActive ? 'active' : ''}
            onClick={() => onSelectModule(homeModule)}
            aria-label="Domů"
          >
            <span className="feather-icon"><i data-feather="home"></i></span>
            <small>Domů</small>
          </button>
        )}
        {showTasks && (
          <button type="button" className={isTasksActive ? 'active' : ''} onClick={onOpenTasks} aria-label="Úkoly">
            <span className="feather-icon p1-mobile-dock-icon"><i data-feather="check-square"></i></span>
            <small>Úkoly</small>
            {openTaskCount > 0 && <b className="p1-mobile-dock-badge">{openTaskCount > 99 ? '99+' : openTaskCount}</b>}
          </button>
        )}
        <button type="button" className="p1-mobile-dock-menu" onClick={() => setMobileMenuOpen(true)} aria-label="Otevřít hlavní menu">
          <span className="p1-mobile-dock-menu-icon"><span className="feather-icon"><i data-feather="menu"></i></span></span>
          <small>Menu</small>
        </button>
        <button type="button" onClick={() => setSearchOpen(true)} aria-label="Hledat">
          <span className="feather-icon"><i data-feather="search"></i></span>
          <small>Hledat</small>
        </button>
        {showHelpdesk && (
          <button type="button" className={isHelpdeskActive ? 'active' : ''} onClick={onOpenHelpdesk} aria-label="Helpdesk">
            <span className="feather-icon p1-mobile-dock-icon"><i data-feather="life-buoy"></i></span>
            <small>Helpdesk</small>
            {waitingHelpdeskCount > 0 && (
              <b className="p1-mobile-dock-badge">{waitingHelpdeskCount > 99 ? '99+' : waitingHelpdeskCount}</b>
            )}
          </button>
        )}
      </nav>
      </div>
    </>
  );
}
